import { FC, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useHotkeys } from 'react-hotkeys-hook';
import { useRootBodyScrollLock } from '../../hooks';
import { Portal } from '../Portal';
import { File as GQLFile } from '../../../generated/graphql';
import { DocumentViewerHeader } from './DocumentViewerHeader';
import { DocumentViewerNav } from './DocumentViewerNav';
import { DocumentViewerPane } from './DocumentViewerPane';
import { useDocumentViewerFile } from './useDocumentViewerFile';

export interface DocumentViewerProps {
  /** 1..n files to view. Pass a single-element array for single-document mode. */
  files: GQLFile[];
  /** Which file in `files` to open on. */
  initialFileID: string;
  tenantID: string;
  open: boolean;
  onClose: () => void;
  /** Optional analytics hook, called when the download button is used. */
  onDownload?: (file: GQLFile) => void;
}

export const DocumentViewer: FC<DocumentViewerProps> = ({ files, initialFileID, tenantID, open, onClose, onDownload }) => (
  <Portal>
    <AnimatePresence>
      {open && files.length > 0 && (
        <DocumentViewerContent
          files={files}
          initialFileID={initialFileID}
          tenantID={tenantID}
          onClose={onClose}
          onDownload={onDownload}
        />
      )}
    </AnimatePresence>
  </Portal>
);

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

interface DocumentViewerContentProps extends Omit<DocumentViewerProps, 'open'> {}

// Only mounted while `open` is true, so `currentFileID` is naturally reset to
// `initialFileID` on every fresh open via the unmount/remount that AnimatePresence does.
const DocumentViewerContent: FC<DocumentViewerContentProps> = ({
  files,
  initialFileID,
  tenantID,
  onClose,
  onDownload,
}) => {
  // Tracked by ID, not index, so paging stays on the same file if `files`
  // reorders (e.g. a background refetch) while the viewer is open.
  const [currentFileID, setCurrentFileID] = useState(initialFileID);

  useRootBodyScrollLock(true);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    container.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === container || document.activeElement === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  const hasMultipleFiles = files.length > 1;
  const foundIndex = files.findIndex((file) => file.id === currentFileID);
  const safeIndex = foundIndex === -1 ? 0 : foundIndex;
  const currentFile = files[safeIndex];
  const { downloadURL, isLoading, isError, retry } = useDocumentViewerFile(tenantID, currentFile);

  const goToPrevious = () => setCurrentFileID(files[safeIndex === 0 ? files.length - 1 : safeIndex - 1].id);
  const goToNext = () => setCurrentFileID(files[safeIndex === files.length - 1 ? 0 : safeIndex + 1].id);

  useHotkeys('escape', onClose, { enableOnFormTags: true });
  useHotkeys('left', goToPrevious, { enabled: hasMultipleFiles });
  useHotkeys('right', goToNext, { enabled: hasMultipleFiles });

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      role="dialog"
      aria-modal="true"
      aria-label={currentFile.name}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-darkMode-gray-dark outline-none"
    >
      <DocumentViewerHeader
        file={currentFile}
        downloadURL={downloadURL}
        currentIndex={safeIndex}
        totalCount={files.length}
        onClose={onClose}
        onDownload={onDownload}
      />
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-6">
        {hasMultipleFiles && <DocumentViewerNav direction="previous" onClick={goToPrevious} />}
        <DocumentViewerPane
          key={currentFile.id}
          file={currentFile}
          downloadURL={downloadURL}
          isLoading={isLoading}
          isError={isError}
          retry={retry}
        />
        {hasMultipleFiles && <DocumentViewerNav direction="next" onClick={goToNext} />}
      </div>
    </motion.div>
  );
};
